import type { Form } from "@tellescope/types-models";

/** English is the source language every form is authored in, so it is never translated. */
const SOURCE_LANGUAGE = "en";

/**
 * Narrows a language tag to the bare code the backend keys translations by, so an active language of
 * `es-MX` matches a configuration stored under `es`.
 */
export const baseLanguageCode = (language: string | undefined): string =>
  (language ?? "").split("-")[0]?.trim().toLowerCase() ?? "";

/**
 * Finds the configuration holding this form's translations for the active language, or `undefined`
 * when there is nothing to fetch — English, or a language the form has no translation for. Callers
 * treat `undefined` as "render the authored English".
 */
export const resolveFormTranslationConfigurationId = (
  form: Pick<Form, "translationConfigurations"> | undefined,
  language: string | undefined
): string | undefined => {
  const code = baseLanguageCode(language);
  if (!code || code === SOURCE_LANGUAGE) return undefined;

  return form?.translationConfigurations?.find(({ language: configured }) => baseLanguageCode(configured) === code)
    ?.configurationId;
};

/**
 * The languages this form can be read in: English plus each language it has a translation configured
 * for. A form with no configured translations yields `["en"]` alone, which is the wizard's signal that
 * there is nothing to choose and no selector to render.
 */
export const resolveFormTranslationLanguages = (
  form: Pick<Form, "translationConfigurations"> | undefined
): string[] => {
  const configured = (form?.translationConfigurations ?? []).reduce<string[]>((acc, { language }) => {
    const code = baseLanguageCode(language);
    if (code && code !== SOURCE_LANGUAGE) acc.push(code);
    return acc;
  }, []);

  return [SOURCE_LANGUAGE, ...new Set(configured)];
};
